import { Component } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { AppService } from '../../services/app.service';
import { EmployeeApplicationComponentBase, IFileMultipleField } from './EmployeeApplicationComponentBase';
import { MessageService } from '../../services/message.service';
import { IFieldFile } from '../../models/IFieldFile';
import { map } from 'rxjs/operators';
import { IEmployeeApplicationCustomField, IEmployeeApplicationCustomForm } from '../../models/EmployeeApplication';
import { EmployeeApplicationService } from '../../services/employee-application.service';

export const parameterCodes = {
    attachmentExtensions: 'EmployeeApplicationFileExtensions',
    attachmentMaxSize: 'EmployeeApplicationFileMaxSize',
    attachmentLimit: 'EmployeeApplicationFileLimit'
};

interface IField {
    config: IEmployeeApplicationCustomField;
    title: string;
    value?: any;
    choices?: any[];
    choicesFn?: (term: string) => Observable<{ value: any, text: string }[]>;
}

/**
 * Custom employee application form
 */
@Component({
    selector: 'app-custom-employee-application',
    templateUrl: './custom-application.component.html',
    styleUrls: ['./application.component.scss']
})
export class CustomEmployeeApplicationComponent extends EmployeeApplicationComponentBase {
    constructor(
        protected readonly app: AppService,
        protected readonly service: EmployeeApplicationService,
        protected readonly messages: MessageService
    ) {
        super(app, service);
    }

    fields: { [key: string]: IField } = {};
    fieldRows: IEmployeeApplicationCustomField[][] = [];
    textAfter: string;
    isReady: boolean;

    private initialForm: string;

    ngOnInit() {
        this.service.getCustomFormByCode(this.item.Type, this.employee.Upn).subscribe(form => {
            this.item.ConfigId = form.Id;
            this.buildForm(form);
            this.initialForm = JSON.stringify(this.fields);
            this.isReady = true;

            this.initEvent.emit({
                canDeactivate: () => {
                    if (!this.isSuccess && this.itemChanged()) {
                        const subj = new Subject<boolean>();
                        this.app.confirm(this.app.translate('employeeApplication_confirmUnsaved'), result => subj.next(result));
                        return subj.asObservable();
                    }

                    return of(true);
                }
            });
        }, err => {
            this.isError = true;
        });

        const messageAfterCode = `EmployeeApplication${this.item.Type}After`;

        this.app.addLoading(this.messages.getText(messageAfterCode, this.app.currentLanguage, true)).subscribe(messages => {
            this.textAfter = messages.find(t => t.Code == messageAfterCode)?.Text;
        });
    }

    canDeactivate(): Observable<boolean> | boolean {
        console.log('canDeactivate.child');
        if (!this.isSuccess && this.itemChanged()) {
            const subj = new Subject<boolean>();
            this.app.confirm(this.app.translate('employeeApplication_confirmUnsaved'), result => subj.next(result));
            return subj.asObservable();
        }

        return of(true);
    }

    protected onSubmit() {
        const subj = new Subject<boolean>();
        this.showFileRequired = false;

        this.confirm('employeeApplication_confirmSubmit', ok => {
            if (ok) {
                const customFields = {};
                Object.keys(this.fields).forEach(t => {
                    customFields[t] = this.fields[t].value || null;
                });

                this.item.CustomFields = customFields;

                this.customFiles = this.fileFieldRows.reduce((arr, t) => {
                    arr.push(...t.files.filter(f => f.file?.size));
                    return arr;
                }, []);
            }

            subj.next(ok);
        });

        return subj.asObservable();
    }

    addAttachment(f: IFileMultipleField) {
        f.files.push(<IFieldFile>{
            field: f.config.Name
        });
    }

    removeAttachment(fileFieldIx: number, fileIx: number) {
        if (this.fileFieldRows[fileFieldIx].files.length != 1) {
            this.fileFieldRows[fileFieldIx].files.splice(fileIx, 1);
        }
    }

    onFileChange(event: File, fileFieldIx: number, fileIx: number) {
        if (!event) {
            this.removeAttachment(fileFieldIx, fileIx);
        }
    }

    private buildForm(form: IEmployeeApplicationCustomForm) {
        const isEn = this.app.currentLanguage === 'en';

        this.fields = {};

        const fieldGroups: { [key: string]: IEmployeeApplicationCustomField[] } = {};
        form.Fields.forEach(t => {
            const rowName = `r${t.Row}`; // prefix added to prevent from auto-sorting in case rows are named as numbers
            const field: IField = this.fields[t.Name] = {
                config: t,
                title: isEn ? t.NameEN : t.NameLV,
                choices: [],
                value: ''
            };

            if (t.Source) {
                const ds = form.DataSources.find(d => d.Name === t.Source.Name);

                if (ds) {
                    if (ds.Type == 'List') {
                        if (t.Type == 'Autocomplete') {
                            field.choicesFn = term => {
                                return this.service.getCustomFormChoices(
                                    this.item.Type,
                                    ds.Name,
                                    term || '').pipe(map(data => {
                                        return (data || []).map(n => {
                                            return {
                                                text: <string>n[t.Source.DisplayField],
                                                value: n[t.Source.ValueField]
                                            };
                                        });
                                    }));
                            };
                        } else {
                            field.choices = ds.Data.map(c => {
                                return {
                                    text: c[t.Source.DisplayField],
                                    value: c[t.Source.ValueField]
                                };
                            });
                        }
                    } else if (ds.Type == 'Dictionary') {
                        if (ds.Data && ds.Data.length) {
                            field.value = ds.Data[0][t.Source.ValueField];
                        }
                    }
                }
            }

            if (!fieldGroups[rowName]) {
                fieldGroups[rowName] = [];
            }

            fieldGroups[rowName].push(t);
        });

        const fieldRows: IEmployeeApplicationCustomField[][] = [];
        for (let g in fieldGroups) {
            fieldRows.push(fieldGroups[g]);
        }

        this.fieldRows = fieldRows;

        if (form.FileFields) {
            this.fileFieldRows = form.FileFields.map(t => {
                const obj = {
                    config: t,
                    title: isEn ? t.NameEN : t.NameLV,
                    files: []
                };

                this.addAttachment(obj);

                return obj;
            }).sort((a, b) => parseInt(a.config.Row) - parseInt(b.config.Row));
        }
    }

    private confirm(text: string, callback: (ok: boolean) => any) {
        this.app.confirm({
            text: this.app.translate(text)
        }, ok => callback(ok));
    }

    private itemChanged(): boolean {
        return !!(this.fileFieldRows.some(t => t.files.some(f => !!f.file)) || this.initialForm !== JSON.stringify(this.fields));
    }
}
